import React, { useState, useCallback } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Box,
    useTheme
} from '@mui/material';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { CancelButton, ActionButton } from '../common/ActionButtons';
import { useTranslation } from 'react-i18next';

interface AvatarCropDialogProps {
    open: boolean;
    imageFile: File | null;
    onClose: () => void;
    onSave: (croppedImageBlob: Blob) => void;
}

const AvatarCropDialog: React.FC<AvatarCropDialogProps> = ({
    open,
    imageFile,
    onClose,
    onSave
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const [imageSrc, setImageSrc] = useState<string>('');
    const [crop, setCrop] = useState<Crop>({
        unit: '%',
        width: 90,
        height: 90,
        x: 5,
        y: 5
    });
    const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
    const [imageRef, setImageRef] = useState<HTMLImageElement | null>(null);
    const [initialCropSet, setInitialCropSet] = useState<boolean>(false);

    React.useEffect(() => {
        if (imageFile && open) {
            const reader = new FileReader();
            reader.onload = () => {
                setImageSrc(reader.result as string);
            };
            reader.readAsDataURL(imageFile);
        }
    }, [imageFile, open]);

    const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
        const img = e.currentTarget;
        setImageRef(img);
        
        if (!initialCropSet) {
            const width = img.width;
            const height = img.height;
            const size = Math.min(width, height);
            const x = (width - size) / 2;
            const y = (height - size) / 2;
            
            const percentCrop: Crop = {
                unit: '%',
                x: (x / width) * 100,
                y: (y / height) * 100,
                width: (size / width) * 100,
                height: (size / height) * 100
            };
            
            setCrop(percentCrop);
            
            const pixelCrop: PixelCrop = {
                unit: 'px',
                x: x,
                y: y,
                width: size,
                height: size
            };
            
            setCompletedCrop(pixelCrop);
            setInitialCropSet(true);
        }
    }, [initialCropSet]);

    const getCroppedImg = useCallback(async (): Promise<Blob | null> => {
        if (!imageRef || !completedCrop) return null;

        const canvas = document.createElement('canvas');
        const scaleX = imageRef.naturalWidth / imageRef.width;
        const scaleY = imageRef.naturalHeight / imageRef.height;

        canvas.width = 256;
        canvas.height = 256;

        const ctx = canvas.getContext('2d');
        if (!ctx) return null;

        ctx.imageSmoothingQuality = 'high';

        ctx.drawImage(
            imageRef,
            completedCrop.x * scaleX,
            completedCrop.y * scaleY,
            completedCrop.width * scaleX,
            completedCrop.height * scaleY,
            0,
            0,
            256,
            256
        );

        return new Promise((resolve) => {
            canvas.toBlob(
                (blob) => {
                    resolve(blob);
                },
                'image/png',
                1
            );
        });
    }, [imageRef, completedCrop]);

    const handleSave = async () => {
        const croppedBlob = await getCroppedImg();
        if (croppedBlob) {
            onSave(croppedBlob);
            handleClose();
        }
    };

    const handleClose = () => {
        setImageSrc('');
        setCrop({
            unit: '%',
            width: 90,
            height: 90,
            x: 5,
            y: 5
        });
        setCompletedCrop(null);
        setImageRef(null);
        setInitialCropSet(false);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                style: {
                    borderRadius: '12px',
                }
            }}
        >
            <DialogTitle sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 700 }}>
                {t('avatarCrop.title')}
            </DialogTitle>
            <DialogContent>
                {imageSrc && (
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center',
                        '& .ReactCrop': {
                            maxHeight: '400px'
                        }
                    }}>
                        <ReactCrop
                            crop={crop}
                            onChange={(c) => setCrop(c)}
                            onComplete={(c) => setCompletedCrop(c)}
                            aspect={1}
                            circularCrop={false}
                        >
                            <img
                                src={imageSrc}
                                alt="Crop"
                                onLoad={onImageLoad}
                                style={{ maxWidth: '100%', maxHeight: '400px' }}
                            />
                        </ReactCrop>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ padding: '16px 24px 24px', justifyContent: 'flex-end', gap: '12px' }}>
                <CancelButton onClick={handleClose}>
                    {t('common.cancel')}
                </CancelButton>
                <ActionButton onClick={handleSave}>
                    {t('common.saveChanges')}
                </ActionButton>
            </DialogActions>
        </Dialog>
    );
};

export default AvatarCropDialog;
